import {
  fetchBlocksByStudySessionId,
  fetchTodayStudySession,
  fetchVignettesForBlock,
} from '../services/study';
import {
  selectActiveBlock,
  selectBlocks,
  selectNextActiveBlock,
} from '../slices/blocks/blocksSlice';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import { updateStudySession } from '../services/studySession';
import { store } from '../store/store';
import { getBlockByIdForReview, updateBlock } from '../services/blocks';
import {
  selectCompletedFlashcardsCount,
  selectDueFlashcards,
  selectNewFlashcards,
} from '../slices/flashcards/flashcardsSlice';
import { fetchFlashcardsForStudySession } from '../services/study';
import { posthog } from 'posthog-js';
import { setActiveQuestionIndex } from '../slices/study/studySlice';

export const loadBlockStudy = async () => {
  try {
    await fadeDelay();
    markTodaySessionAsStarted();
    await checkRefreshContent();
    const activeBlock = selectActiveBlock(store.getState());
    if (!activeBlock || activeBlock.isSubmitted) {
      await transitionToNextBlock();
    }
    await markActiveBlockAsStarted();
  } catch (error) {
    console.error('Error loading block study:', error);
    throw error;
  }
};

export const loadBlockReview = async (blockId: string) => {
  try {
    await fadeDelay();
    resetActiveQuestionIndex();
    const blocks = selectBlocks(store.getState());
    const isCurrentSessionBlock = blocks.some((b) => b.id === blockId);

    if (isCurrentSessionBlock) {
      const todaySession = selectTodaySession(store.getState());
      await Promise.all([
        updateStudySession({
          id: todaySession.id,
          activeBlockId: blockId,
        }),
        fetchVignettesForBlock(blockId),
      ]);
    } else {
      await getBlockByIdForReview(blockId);
    }
  } catch (error) {
    console.error('Error loading block review:', error);
    throw error;
  }
};

export const loadBlockSelect = async () => {
  try {
    await fadeDelay();

    const activeBlock = selectActiveBlock(store.getState());
    if (!activeBlock || activeBlock.isSubmitted) {
      await transitionToNextBlock();
    }
  } catch (error) {
    console.error('Error loading block select:', error);
    throw error;
  }
};

export const loadFlashcardStudy = async () => {
  try {
    await fadeDelay();

    markTodaySessionAsStarted();
    await checkRefreshContent();
  } catch (error) {
    console.error('Error loading flashcard study:', error);
    throw error;
  }
};

export const loadVideoStudy = async () => {
  try {
    await fadeDelay();
    markTodaySessionAsStarted();
  } catch (error) {
    console.error('Error loading video study:', error);
    throw error;
  }
};

export const loadSessionSummary = async () => {
  try {
    await fadeDelay();

    await markTodaySessionAsComplete();
  } catch (error) {
    console.error('Error loading session summary:', error);
    throw error;
  }
};

export const loadPastBlocks = async () => {
  try {
    await fadeDelay();
    resetActiveQuestionIndex();
    await forceRefreshBlocksAndVignettes();
  } catch (error) {
    console.error('Error loading past blocks:', error);
    throw error;
  }
};

export const loadHome = async () => {
  try {
    await fadeDelay();
  } catch (error) {
    console.error('Error loading home:', error);
    throw error;
  }
};

export const markTodaySessionAsComplete = async () => {
  const todaySession = selectTodaySession(store.getState());
  if (todaySession.id && !todaySession.isComplete) {
    posthog?.capture('study_session_complete');
    await updateStudySession({
      id: todaySession.id,
      isComplete: true,
    });
  }
};

export const markActiveBlockAsStarted = async () => {
  const state = store.getState();
  const activeBlock = selectActiveBlock(state);
  if (activeBlock?.id && !activeBlock.isStarted) {
    posthog?.capture(activeBlock.type + '_block_start');
    await updateBlock({
      blockId: activeBlock.id,
      isStarted: true,
    });
    resetActiveQuestionIndex();
  }
};

export const transitionToNextBlock = async () => {
  const state = store.getState();
  const todaySession = selectTodaySession(state);
  const activeBlock = selectActiveBlock(state);
  const nextBlock = selectNextActiveBlock(state);

  if (nextBlock && nextBlock.id !== activeBlock?.id && todaySession?.id) {
    await Promise.all([
      updateStudySession({
        id: todaySession.id,
        activeBlockId: nextBlock.id,
      }),
      fetchVignettesForBlock(nextBlock.id),
    ]);
  }
};

export const forceRefreshBlocksAndVignettes = async () => {
  const studySession = await fetchTodayStudySession();
  await fetchBlocksByStudySessionId(studySession.id);
  const activeBlockId = studySession.activeBlockId;
  if (activeBlockId) {
    await fetchVignettesForBlock(activeBlockId);
  }
};

export const checkRefreshContent = async () => {
  const todaySession = selectTodaySession(store.getState());
  if (!todaySession.id) {
    await fetchTodayStudySession();
  }
  if (todaySession.isUsingQbank) {
    const activeBlock = selectActiveBlock(store.getState());
    if (!activeBlock) {
      await fetchBlocksByStudySessionId(todaySession.id);
      await transitionToNextBlock();
    }
  }
  if (todaySession.isUsingFlashcards) {
    const newFlashcards = selectNewFlashcards(store.getState());
    const dueFlashcards = selectDueFlashcards(store.getState());
    const completedFlashcardsCount = selectCompletedFlashcardsCount(
      store.getState()
    );
    const flashcardCount = todaySession?.flashcardCount;
    if (
      newFlashcards.length === 0 &&
      dueFlashcards.length === 0 &&
      flashcardCount > completedFlashcardsCount
    ) {
      await fetchFlashcardsForStudySession(todaySession.id);
    }
  }
};

const markTodaySessionAsStarted = async () => {
  const todaySession = selectTodaySession(store.getState());
  if (todaySession && !todaySession.isStarted) {
    posthog?.capture('study_session_start');
    posthog?.capture('daily_study_session_start');
    await updateStudySession({
      id: todaySession.id,
      isStarted: true,
    });
  }
};

export const resetActiveQuestionIndex = () => {
  store.dispatch(setActiveQuestionIndex(0));
};

// Add small delay to allow for smooth fading transitions
const fadeDelay = async (ms: number = 250) => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};
